import platformFaviconUrl from '@EPImages/favicon.ico';
import { resolveFilesPublicPath } from '@ep/tools/filesPublicPath';

const isRecord = (value) => value !== null && typeof value === 'object' && !Array.isArray(value);
const compact = (value) => String(value ?? '').trim();

export default (seo) => {
  const head = document.head;
  if (!head) return;

  const ensureTitle = () => {
    let el = document.querySelector('title');
    if (!el) {
      el = document.createElement('title');
      head.appendChild(el);
    }
    return el;
  };
  const ensureMeta = (attribute, key) => {
    let el = document.querySelector(`meta[${attribute}="${key}"]`);
    if (!el) {
      el = document.createElement('meta');
      el.setAttribute(attribute, key);
      head.appendChild(el);
    }
    return el;
  };
  const ensureLink = (rel) => {
    let el = document.querySelector(`link[rel="${rel}"]`);
    if (!el) {
      el = document.createElement('link');
      el.rel = rel;
      head.appendChild(el);
    }
    return el;
  };
  const setMeta = (attribute, key, value) => {
    ensureMeta(attribute, key).setAttribute('content', compact(value));
  };
  const remove = (selector) => document.querySelector(selector)?.remove();
  const currentHost = compact(window.location?.hostname).toLowerCase();
  const isPreview = /^preview-[a-z0-9-]+\.admindigital\.(loc|test|com|app|com\.br)$/i.test(currentHost)
    || seo?.preview === true;
  const invalidSeo = !isRecord(seo) || seo.enabled === false || seo.robots === false;
  const favicon = ensureLink('icon');
  favicon.setAttribute('href', compact(seo?.favicon) || platformFaviconUrl);

  const clearRichSeo = () => {
    remove('link[rel="canonical"]');
    ['og:title', 'og:description', 'og:image', 'og:image:alt', 'og:url', 'og:type', 'og:site_name', 'og:locale']
      .forEach((name) => remove(`meta[property="${name}"]`));
    ['twitter:card', 'twitter:site', 'twitter:creator', 'twitter:title', 'twitter:description', 'twitter:image', 'twitter:image:alt']
      .forEach((name) => remove(`meta[name="${name}"]`));
    remove('script[type="application/ld+json"]');
  };

  if (invalidSeo) {
    ensureTitle().textContent = compact(seo?.title);
    setMeta('name', 'robots', 'noindex, nofollow, noarchive, nosnippet, noimageindex');
    ['description', 'keywords'].forEach((name) => remove(`meta[name="${name}"]`));
    clearRichSeo();
    return;
  }

  if (isPreview) {
    ensureTitle().textContent = compact(seo.title);
    setMeta('name', 'robots', 'noindex, nofollow, noarchive, nosnippet, noimageindex');
    if (compact(seo.description)) setMeta('name', 'description', seo.description);
    else remove('meta[name="description"]');
    remove('meta[name="keywords"]');
    clearRichSeo();
    return;
  }

  const canonicalBase = compact(seo.canonical);
  let canonical;
  try {
    const base = canonicalBase ? new URL(canonicalBase) : new URL(window.location.origin);
    canonical = new URL(window.location.pathname || '/', base.origin);
    canonical.search = '';
    canonical.hash = '';
  } catch {
    canonical = new URL(window.location.pathname || '/', window.location.origin);
  }
  const canonicalUrl = canonical.toString();
  const robots = isRecord(seo.robots) ? seo.robots : {};
  const directives = [
    robots.index === false ? 'noindex' : 'index',
    robots.follow === false ? 'nofollow' : 'follow',
    ...(robots.noarchive ? ['noarchive'] : []),
    ...(robots.nosnippet ? ['nosnippet'] : []),
    ...(robots.noimageindex ? ['noimageindex'] : []),
  ];
  const og = isRecord(seo.og) ? seo.og : {};
  const twitter = isRecord(seo.twitter) ? seo.twitter : {};
  const ogImage = resolveFilesPublicPath(og.image);
  const twitterImage = resolveFilesPublicPath(twitter.image || og.image);
  const schema = isRecord(seo.schema) ? seo.schema : {};
  let jsonLd = null;
  if (schema.enabled !== false && schema.json) {
    try {
      jsonLd = typeof schema.json === 'string' ? JSON.parse(schema.json) : schema.json;
    } catch {
      jsonLd = null;
    }
  }

  ensureTitle().textContent = compact(seo.title);
  setMeta('name', 'description', seo.description);
  setMeta('name', 'keywords', seo.keywords);
  setMeta('name', 'robots', directives.join(', '));
  ensureLink('canonical').setAttribute('href', canonicalUrl);
  setMeta('property', 'og:type', og.type || 'website');
  setMeta('property', 'og:site_name', og.site_name);
  setMeta('property', 'og:locale', og.locale);
  setMeta('property', 'og:title', og.title || seo.title);
  setMeta('property', 'og:description', og.description || seo.description);
  setMeta('property', 'og:url', canonicalUrl);
  setMeta('property', 'og:image', ogImage);
  setMeta('property', 'og:image:alt', og.image_alt);
  setMeta('name', 'twitter:card', twitter.card || 'summary');
  setMeta('name', 'twitter:site', twitter.site);
  setMeta('name', 'twitter:creator', twitter.creator);
  setMeta('name', 'twitter:title', twitter.title || og.title || seo.title);
  setMeta('name', 'twitter:description', twitter.description || og.description || seo.description);
  setMeta('name', 'twitter:image', twitterImage);
  setMeta('name', 'twitter:image:alt', twitter.image_alt || og.image_alt);

  if (jsonLd && isRecord(jsonLd)) {
    let script = document.querySelector('script[type="application/ld+json"]');
    if (!script) {
      script = document.createElement('script');
      script.type = 'application/ld+json';
      head.appendChild(script);
    }
    script.textContent = JSON.stringify(jsonLd);
  } else {
    remove('script[type="application/ld+json"]');
  }
};
